import { useState, useEffect, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Star } from "lucide-react";

export const punkteStufe = (p = 0) => {
  if (p >= 5) return { label: "Positiv", cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/40" };
  if (p < 0) return { label: "Kritisch", cls: "bg-red-500/15 text-red-400 border-red-500/40" };
  return { label: "Neutral", cls: "bg-zinc-500/15 text-zinc-300 border-zinc-500/40" };
};

const fmt = (iso) => new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) + " Uhr";

export const PunkteBadge = ({ punkte = 0, testid }) => {
  const s = punkteStufe(punkte);
  return (
    <Badge data-testid={testid} className={`rounded-sm border font-mono text-[10px] gap-1 ${s.cls}`}>
      <Star className="h-3 w-3" /> {punkte > 0 ? `+${punkte}` : punkte} · {s.label}
    </Badge>
  );
};

export const LangzeitPunkte = ({ haeftlingId, onChange }) => {
  const [list, setList] = useState([]);
  const [wert, setWert] = useState("");
  const [grund, setGrund] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/langzeithaeftlinge/${haeftlingId}/punkte`);
    setList(data);
    onChange(data.reduce((s, b) => s + b.wert, 0));
  }, [haeftlingId, onChange]);

  useEffect(() => { load().catch(() => toast.error("Punkte laden fehlgeschlagen")); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    const n = parseInt(wert, 10);
    if (!n || !grund.trim()) { toast.error("Wert (nicht 0) und Grund angeben"); return; }
    setBusy(true);
    try {
      await api.post(`/langzeithaeftlinge/${haeftlingId}/punkte`, { wert: n, grund });
      setWert(""); setGrund("");
      toast.success("Punkte gebucht");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Buchung fehlgeschlagen");
    } finally { setBusy(false); }
  };

  const remove = async (b) => {
    if (!window.confirm("Buchung wirklich löschen?")) return;
    try {
      await api.delete(`/langzeithaeftlinge/${haeftlingId}/punkte/${b.id}`);
      toast.success("Buchung gelöscht");
      await load();
    } catch { toast.error("Löschen fehlgeschlagen"); }
  };

  return (
    <div data-testid="lz-punkte-section" className="border border-zinc-800 rounded-sm p-3 space-y-3">
      <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-zinc-600">Punkteverlauf</div>
      <form onSubmit={add} className="flex flex-col sm:flex-row gap-2">
        <Input data-testid="lz-punkte-wert" type="number" step="1" value={wert} onChange={(e) => setWert(e.target.value)} placeholder="z. B. +3 / -2" className="sm:w-32 bg-transparent border-zinc-700 rounded-sm" />
        <Input data-testid="lz-punkte-grund" value={grund} onChange={(e) => setGrund(e.target.value)} placeholder="Grund" maxLength={500} className="flex-1 bg-transparent border-zinc-700 rounded-sm" />
        <Button data-testid="lz-punkte-add" type="submit" disabled={busy} className="rounded-sm bg-primary hover:bg-blue-600 text-white gap-1.5"><Plus className="h-4 w-4" /> Buchen</Button>
      </form>
      {list.length === 0 ? (
        <div className="text-xs text-zinc-600 font-mono">Noch keine Buchungen</div>
      ) : (
        <ul className="space-y-1.5 max-h-60 overflow-y-auto">
          {list.map((b) => (
            <li key={b.id} data-testid="lz-punkte-entry" className="flex items-start gap-3 text-sm bg-black/20 rounded-sm px-2 py-1.5">
              <span className={`font-mono font-bold w-12 shrink-0 ${b.wert > 0 ? "text-emerald-400" : "text-red-400"}`}>{b.wert > 0 ? `+${b.wert}` : b.wert}</span>
              <div className="flex-1 min-w-0">
                <div className="text-zinc-200 whitespace-pre-wrap break-words">{b.grund}</div>
                <div className="text-[11px] text-zinc-500 font-mono">{b.created_by || "—"} · {fmt(b.created_at)}</div>
              </div>
              <button type="button" data-testid="lz-punkte-delete" onClick={() => remove(b)} title="Buchung löschen" className="text-zinc-500 hover:text-red-400 transition-colors duration-150"><Trash2 className="h-4 w-4" /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
